import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import React, { useEffect, useState } from "react"
import { SEO } from "../../components/SEO"
import { PATHS } from "../../constants/paths"
import Button from "@mui/material/Button"
import { Link, useParams } from "react-router-dom"
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import Box from "@mui/material/Box"
import { Container } from "@mui/material"
import Typography from "@mui/material/Typography"
import { StatCard } from "./components/StatCard"
import Overview from "./components/tabs/Overview"
import Leaderboard from "./components/tabs/Leaderboard"
import Prizes from "./components/tabs/Prizes"
import Rules from "./components/tabs/Rules"
import { API_URL } from "../../constants/constants"
import NotFoundPage from "../NotFoundPage"
import { formatCountdown } from "./utils/formatCountdown"
import LoadingButton from "@mui/lab/LoadingButton"
import { useAppDispatch, useAppSelector } from "../../store"
import { connectHandler } from "../../utils/connectHandler"
import JoinConfirmDialog from "./components/dialogs/JoinConfirmDialog"
import { useDialog } from "../../context/DialogContext"
import GameDialog from "./components/dialogs/GameDialog"
import { TournamentResultDialog } from "./components/dialogs/TournamentResultDialog"

const tabs = [
  {
    id: "overview",
    label: "OVERVIEW",
  },
  {
    id: "leaderboard",
    label: "LEADERBOARD",
  },
  {
    id: "prizes",
    label: "PRIZES",
  },
  {
    id: "rules",
    label: "RULES",
  },
]

export const TournamentDetailsPage = () => {
  const { id } = useParams()

  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.user)

  const [activeTab, setActiveTab] = useState("overview")
  const [tournament, setTournament] = useState(null)
  const [leaderboard, setLeaderboard] = useState(null)
  const [prizes, setPrizes] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [remainingSeconds, setRemainingSeconds] = useState()
  const [createDialog, closeDialog] = useDialog()
  const [openGameDialog, closeGameDialog] = useDialog()
  const [openTournamentResultDialog, closeTournamentResultDialog] = useDialog()

  const openTournamentGameDialog = (result = {}) => {
    openGameDialog({
      disableBackdropClick: true,
      disableEscapeKeyDown: true,
      children: (
        <GameDialog
          tournamentId={id}
          user={user}
          closeDialog={closeGameDialog}
          onGameFinished={openResultDialog}
        />
      ),
    })
  }

  const openResultDialog = (result = {}) => {
    openTournamentResultDialog({
      children: (
        <TournamentResultDialog
          closeDialog={closeTournamentResultDialog}
          playAgain={openGameDialog}
          zombiesKilled={result.zombiesKilled}
          waveReached={result.waveReached}
          bossesKilled={result.bossesKilled}
          score={result.score}
          timePlayed={result.timePlayed}
          position={result.position}
          totalPlayers={result.totalPlayers}
          bestScore={result.bestScore}
          variant={result.variant || "complete"}
        />
      ),
    })
  }

  useEffect(() => {
    if (!tournament) {
      return
    }

    setRemainingSeconds(
      Number(tournament.time_left ?? tournament.starts_in ?? 0),
    )
  }, [tournament])

  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingSeconds((prevSeconds) => {
        if (prevSeconds <= 0) {
          return 0
        }

        return prevSeconds - 1
      })
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    loadTournament()
  }, [user])

  const loadTournament = async () => {
    setIsLoading(true)

    try {
      const response = await fetch(`${API_URL}/tournaments/details/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_wallet: user.address,
        }),
      })

      if (!response.ok) {
        return
      }

      const data = await response.json()

      if (data.success) {
        setTournament(data.tournament)
        setLeaderboard(data.leaderboard)
        setPrizes(data.prizes)
      }
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return null
  }

  if (!tournament) {
    return <NotFoundPage />
  }

  return (
    <Box>
      <SEO
        title={`Zombie TD | ${tournament.title} Tournament`}
        description={`Join the ${tournament.title} tournament, compete with players and win USDT rewards.`}
        ogTitle={`Zombie TD | ${tournament.title} Tournament`}
        ogDescription="Top players. Bigger prize pool. Only the strongest survivors will claim the rewards."
        ogUrl={`${PATHS.TOURNAMENT_DETAILS.PATH}`}
      />
      <Box className="content">
        <Box className="content-head" mb={4} mt={2}>
          <Container fixed>
            {/* Back button */}
            <Button
              component={Link}
              to={PATHS.TOURNAMENTS.PATH}
              startIcon={<ArrowBackIosNewOutlinedIcon />}
              sx={{
                px: 0,
                color: "#8f877b",
                fontWeight: 900,
                "& .MuiButton-startIcon": {
                  mr: 0.5,
                },
                "& svg": {
                  fontSize: "12px !important",
                },
                "&:hover": {
                  color: "#d69a49",
                  background: "transparent",
                },
              }}
            >
              BACK TO TOURNAMENTS
            </Button>

            <Box
              sx={{
                background: `url(${tournament.image_hero}) center / cover no-repeat, #000`,
                px: 4,
                py: 2,
              }}
            >
              <Typography variant="h1">{tournament.title}</Typography>

              <Typography
                variant="subtitle1"
                textAlign="left"
                sx={{ mb: 2, fontSize: 16, color: "#dedede", maxWidth: "60%" }}
              >
                {tournament.brief}
              </Typography>
            </Box>

            {/* Summary statistics */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 3,
                gap: 1.5,
                mb: 6,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  flexWrap: "wrap",
                  border: "1px solid #121212",
                  borderRadius: 1,
                  gap: 1.5,
                  background: "linear-gradient(180deg, #000 60%, #121212 100%)",
                }}
              >
                {[
                  {
                    icon: <EmojiEventsOutlinedIcon />,
                    label: "PRIZE POOL",
                    value: tournament.prize_pool + " USDT",
                  },
                  {
                    icon: <EmojiEventsOutlinedIcon />,
                    label: "ENTRY FEE",
                    value: tournament.entry_fee + " USDT",
                  },
                  {
                    icon: <GroupsOutlinedIcon />,
                    label: "PLAYERS",
                    value: tournament.players,
                    focus: false,
                  },
                  {
                    icon: <AccessTimeOutlinedIcon />,
                    label: tournament.is_finished
                      ? "FINISHED"
                      : tournament.starts_in
                        ? "STARTS IN"
                        : "TIME LEFT",
                    value: tournament.is_finished
                      ? tournament.end_at
                      : formatCountdown(remainingSeconds, true),
                    focus: false,
                  },
                ].map((stat) => (
                  <StatCard {...stat} />
                ))}
              </Box>

              {tournament.is_finished ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.6,
                    color: "#d69a49",
                    textTransform: "uppercase",
                    fontWeight: 900,
                    "& svg": {
                      fontSize: 26,
                    },
                  }}
                >
                  <CheckCircleOutlineIcon /> Finished
                </Box>
              ) : !user.address ? (
                <LoadingButton
                  variant="contained"
                  size="large"
                  sx={{
                    background: "linear-gradient(135deg, #FF6B00, #C13D00)",
                    mt: 2,
                    zIndex: 0,

                    "&::before": {
                      content: '""',
                      borderRadius: "inherit",
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(135deg, rgb(230,85,0), rgb(160,45,0))",
                      opacity: 0,
                      transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                      zIndex: -1,
                    },

                    "&:hover::before": {
                      opacity: 1,
                    },
                  }}
                  onClick={() => connectHandler(dispatch)}
                >
                  Connect wallet
                </LoadingButton>
              ) : (
                <>
                  {tournament.participation ? (
                    <LoadingButton
                      variant="contained"
                      size="large"
                      sx={{
                        background: "linear-gradient(135deg, #39FF14, #1E9600)",
                        mt: 2,
                        zIndex: 0,
                        "&::before": {
                          content: '""',
                          borderRadius: "inherit",
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(135deg, #2bb80e, #146b00)",
                          opacity: 0,
                          transition:
                            "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                          zIndex: -1,
                        },

                        "&:hover::before": {
                          opacity: 1,
                        },
                      }}
                      onClick={(event) => {
                        event.stopPropagation()
                        createDialog({
                          disableBackdropClick: true,
                          disableEscapeKeyDown: true,
                          children: (
                            <GameDialog
                              tournamentId={id}
                              user={user}
                              closeDialog={closeGameDialog}
                              onGameFinished={openResultDialog}
                            />
                          ),
                        })
                      }}
                    >
                      Play Now
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      variant="contained"
                      size="large"
                      sx={{
                        background: "linear-gradient(135deg, #FF6B00, #C13D00)",
                        mt: 2,
                        zIndex: 0,
                        "&::before": {
                          content: '""',
                          borderRadius: "inherit",
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(135deg, rgb(230,85,0), rgb(160,45,0))",
                          opacity: 0,
                          transition:
                            "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                          zIndex: -1,
                        },
                        "&:hover::before": {
                          opacity: 1,
                        },
                      }}
                      onClick={(event) => {
                        event.stopPropagation()
                        openTournamentGameDialog()
                      }}
                    >
                      Join Tournament
                    </LoadingButton>
                  )}
                </>
              )}
            </Box>

            {/* Tabs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2.4,
                overflowX: "auto",
                borderBottom: "1px solid #121212",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  minWidth: "max-content",
                }}
              >
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id

                  return (
                    <Button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      sx={{
                        px: 2.4,
                        py: 1.15,
                        color: isActive ? "#c49654" : "#9c9284",
                        fontWeight: 900,
                        borderRadius: "4px 4px 0 0",
                        border: isActive
                          ? "1px solid rgba(196,150,84,0.1)"
                          : "1px solid transparent",
                        background: isActive
                          ? "rgba(196,150,84,0.06)"
                          : "transparent",
                        "&:hover": {
                          borderColor: "rgba(198,146,76,0.45)",
                          background: "rgba(198,146,76,0.08)",
                        },
                      }}
                    >
                      {tab.label}
                    </Button>
                  )
                })}
              </Box>
            </Box>

            {activeTab === "overview" && <Overview tournament={tournament} />}
            {activeTab === "leaderboard" && (
              <Leaderboard leaderboard={leaderboard} />
            )}
            {activeTab === "prizes" && <Prizes prizes={prizes} />}
            {activeTab === "rules" && <Rules />}
          </Container>
        </Box>
      </Box>
    </Box>
  )
}
